import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { MessageAPI } from '@/services/message'

// 生图记录类型
export interface ImageHistoryItem {
  id: string
  imageUrl: string
  prompt?: string
  createdAt: number
  digitalHumanId?: string
  sessionId?: string
}

// 生视频记录类型
export interface VideoHistoryItem {
  id: string
  videoUrl: string
  coverImage?: string
  prompt?: string
  createdAt: number
  digitalHumanId?: string
  sessionId?: string
}

// 历史记录 Store
export const useHistoryStore = defineStore('history', () => {
  // 生图历史列表
  const imageHistory = ref<ImageHistoryItem[]>([])
  // 生视频历史列表
  const videoHistory = ref<VideoHistoryItem[]>([])
  // 加载状态
  const isLoadingImage = ref(false)
  const isLoadingVideo = ref(false)

  // 计算属性：生图列表（按时间倒序）
  const sortedImageHistory = computed(() => {
    return [...imageHistory.value].sort((a, b) => b.createdAt - a.createdAt)
  })

  // 计算属性：生视频列表（按时间倒序）
  const sortedVideoHistory = computed(() => {
    return [...videoHistory.value].sort((a, b) => b.createdAt - a.createdAt)
  })

  // 从接口获取生图记录
  const fetchImageHistory = async (params?: any) => {
    isLoadingImage.value = true
    try {
      const res = await MessageAPI.getRecordImag(params)
      if (res?.data) {
        imageHistory.value = res.data.map((item: any) => ({
          id: item.id || `img_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
          imageUrl: item.origin || item.url || '',
        }))
      }else{
        imageHistory.value = []
      }
    } catch (e) {
      console.error('获取生图记录失败:', e)
    } finally {
      isLoadingImage.value = false
    }
  }

  // 从接口获取生视频记录
  const fetchVideoHistory = async (params?: any) => {
    isLoadingVideo.value = true
    try {
      const res = await MessageAPI.getRecordImag(params)
      if (res?.data) {
        videoHistory.value = res.data.map((item: any) => ({
          id: item.id || `vid_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
          videoUrl: item.origin || item.url || '',
          coverImage: item.thumbUrl || item.cover || '',
        }))
      }else{
        videoHistory.value = []
      }
    } catch (e) {
      console.error('获取生视频记录失败:', e)
    } finally {
      isLoadingVideo.value = false
    }
  }

  // 添加生图记录
  const addImageHistory = (item: Omit<ImageHistoryItem, 'id' | 'createdAt'>) => {
    const newItem: ImageHistoryItem = {
      ...item,
      id: `img_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      createdAt: Date.now()
    }
    imageHistory.value.unshift(newItem)
    return newItem
  }

  // 添加生视频记录
  const addVideoHistory = (item: Omit<VideoHistoryItem, 'id' | 'createdAt'>) => {
    const newItem: VideoHistoryItem = {
      ...item,
      id: `vid_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      createdAt: Date.now()
    }
    videoHistory.value.unshift(newItem)
    return newItem
  }

  // 删除生图记录
  const removeImageHistory = (id: string) => {
    const index = imageHistory.value.findIndex(item => item.id === id)
    if (index > -1) {
      imageHistory.value.splice(index, 1)
    }
  }

  // 删除生视频记录
  const removeVideoHistory = (id: string) => {
    const index = videoHistory.value.findIndex(item => item.id === id)
    if (index > -1) {
      videoHistory.value.splice(index, 1)
    }
  }

  // 清空生图历史
  const clearImageHistory = () => {
    imageHistory.value = []
  }

  // 清空生视频历史
  const clearVideoHistory = () => {
    videoHistory.value = []
  }

  // 清空所有历史
  const clearAllHistory = () => {
    imageHistory.value = []
    videoHistory.value = []
  }

  // 根据 ID 获取生图记录
  const getImageById = (id: string) => {
    return imageHistory.value.find(item => item.id === id)
  }

  // 根据 ID 获取生视频记录
  const getVideoById = (id: string) => {
    return videoHistory.value.find(item => item.id === id)
  }

  // 获取生图总数
  const imageCount = computed(() => imageHistory.value.length)

  // 获取生视频总数
  const videoCount = computed(() => videoHistory.value.length)

  return {
    // 状态
    imageHistory,
    videoHistory,
    isLoadingImage,
    isLoadingVideo,
    // 计算属性
    sortedImageHistory,
    sortedVideoHistory,
    imageCount,
    videoCount,
    // 方法
    fetchImageHistory,
    fetchVideoHistory,
    addImageHistory,
    addVideoHistory,
    removeImageHistory,
    removeVideoHistory,
    clearImageHistory,
    clearVideoHistory,
    clearAllHistory,
    getImageById,
    getVideoById
  }
})
